/**
 * Small, pure date/time formatters shared across the SPA. No DOM, no deps —
 * just `Intl`/`Date`, with a raw-string fallback so a malformed value never
 * renders "Invalid Date".
 */

/** Render an ISO 8601 timestamp as a short local time, e.g. `00:51:41`. */
export function shortTime(iso: string): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return iso;
  return new Date(ms).toLocaleTimeString();
}

/**
 * Render an ISO 8601 timestamp as a short local date + time, e.g.
 * `Jun 26, 00:51:41` — the short date disambiguates entries that span days
 * while staying terse.
 */
export function shortDateTime(iso: string): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return iso;
  const d = new Date(ms);
  const date = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return `${date}, ${d.toLocaleTimeString()}`;
}

/**
 * Like {@link shortDateTime} but with the year, e.g. `Jun 26, 2026, 00:51:41` —
 * for detail pages (not list rows) where the extra width is fine and the exact
 * date matters. Same raw-string fallback.
 */
export function shortDateTimeWithYear(iso: string): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return iso;
  const d = new Date(ms);
  const date = d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  return `${date}, ${d.toLocaleTimeString()}`;
}
